/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import { Card } from '@fluentui/react-components';
import React, { useEffect, useRef, useState } from 'react';
import confettiAnimationData from '../../../assets/confetti-animation.json';
import checkmarkHeroAnimationData from '../../../assets/progress-animation-v1.json';
import { strings } from '../../intl/Strings';
import { ConfettiHeroAnimations } from '../ConfettiHeroAnimations/ConfettiHeroAnimations';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { TaskList } from '../TaskButtonList/TaskList';
import { useStyles } from './ActionCard.styles';
import {
    areAllTasksCompleted,
    calculateProgress,
    formatProgressMessage,
    getCompletedTaskCount,
    getHeroKeyframe
} from './ActionCard.utils';

export type Action = {
    id: string;
    checked: boolean;
    primaryContent: string;
    secondaryContent: string;
    showSecondaryContent?: boolean;
    onClick?: () => void;
    retriggerable?: boolean;
};

type ActionCardProps = {
    titleLines?: string[];
    actions?: Action[];
    footer?: React.ReactNode;
};

export const ActionCard: React.FC<ActionCardProps> = ({
    actions: passedActions,
    titleLines: passedTitleLines,
    footer
}) => {
    const confettiLottieRef = useRef<any>(null);
    const checkmarkHeroLottieRef = useRef<any>(null);
    const styles = useStyles();
    const titleLines = passedTitleLines ?? [];

    const [actions, setActions] = useState<Action[]>(() => passedActions ?? []);
    const [progress, setProgress] = useState(0.0);
    const [progressMessage, setProgressMessage] = useState('');

    // Keep internal actions state in sync when a parent provides `passedActions`
    useEffect(() => {
        if (passedActions) {
            setActions(passedActions);
        }
    }, [passedActions]);

    useEffect(() => {
        const completedTasks = getCompletedTaskCount(actions);
        const newProgress = calculateProgress(actions);
        const messageTemplate = strings.getString('lftSidePanePromptsExplored');

        setProgress(newProgress);
        setProgressMessage(formatProgressMessage(completedTasks, actions.length, messageTemplate));

        // Animate hero progress
        if (checkmarkHeroLottieRef.current) {
            const keyframe = getHeroKeyframe(actions.length, completedTasks);
            if (keyframe) {
                checkmarkHeroLottieRef.current.playToKeyframe?.(keyframe);
            }
        }

        // Play confetti if all tasks are completed
        if (areAllTasksCompleted(actions)) {
            confettiLottieRef.current?.playAfterDelay?.(500);
        }
    }, [actions]);

    // Handle task button click: mark as completed and call the action's onClick
    const handleTaskClick = (idx: number) => {
        const clickedAction = actions[idx];

        if (clickedAction.checked && !clickedAction.retriggerable) {
            return;
        }

        if (!clickedAction.checked) {
            setActions(prev =>
                prev.map((action, i) =>
                    i === idx ? { ...action, checked: true } : action
                )
            );
        }

        clickedAction.onClick?.();
    };

    return (
        <div className={styles.root}>
            <Card className={styles.card}>
                {titleLines.map((item, index) => (
                    <div key={index} className={styles.title}>{item}</div>
                ))}
                <ConfettiHeroAnimations
                    confettiRef={confettiLottieRef}
                    heroRef={checkmarkHeroLottieRef}
                    confettiAnimationData={confettiAnimationData}
                    heroAnimationData={checkmarkHeroAnimationData}
                />
                <ProgressBar progress={progress} message={progressMessage} />
                <TaskList actions={actions} onTaskClick={handleTaskClick} />
                {footer}
            </Card>
        </div>
    );
};